import React from 'react';

import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';

import CircleXIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleX';
import TriangleAlertIcon from '@liquidweb-dev/undercurrent/icons/lucide/TriangleAlert';
import XIcon from '@liquidweb-dev/undercurrent/icons/lucide/X';

import {
	CRITICAL_SEVERITY,
	ERROR_SEVERITY,
	WARNING_SEVERITY,
} from 'utility/constants/notificationConstants';

/**
 * @typedef SeverityIconProps
 * @property {string} severity
 */

/**
 * @param {SeverityIconProps & import('@liquidweb-dev/undercurrent/types/svgTypes').LucideSvgProps} props
 */
const SeverityIcon = ({ severity, ...rest }) => {
	if ([CRITICAL_SEVERITY, ERROR_SEVERITY].includes(severity)) {
		return (
			<CircleXIcon
				sx={(theme) => ({ color: theme.vars.palette.uc.danger.icon.main })}
				{...rest}
			/>
		);
	}
	if (WARNING_SEVERITY === severity) {
		return (
			<TriangleAlertIcon
				sx={(theme) => ({ color: theme.vars.palette.uc.warning.icon.main })}
				{...rest}
			/>
		);
	}
	return null;
};

const stripHtml = (text) => {
	return text.replace(/<[^>]*>?/g, '');
};

const stripLineBreaks = (text) => {
	return text.replace(/\n/g, '');
};

/**
 * @typedef AlertItemPropsI
 * @property {import('@liquidweb-dev/club-sauce/public/notifications/index.raw').LWApiPublicNotificationDetailsResultRawI} alert
 * @property {() => void} onClick
 * @property {() => void} onDismissClick
 */

/**
 * @param {AlertItemPropsI} props
 */
const AlertItem = ({ alert, onClick, onDismissClick }) => (
	<MenuItem onClick={onClick}>
		<Stack
			direction="row"
			justifyContent="space-between"
			alignItems="center"
			width={1}
			spacing={100}
		>
			<SeverityIcon severity={alert.severity} />
			<Typography variant="label-lg" noWrap={true} flex={1}>
				{stripLineBreaks(stripHtml(alert?.description))}
			</Typography>
			<Tooltip placement="bottom" title="Dismiss" variant="primary">
				<IconButton
					variant="barebones"
					onClick={(event) => {
						event.stopPropagation();
						onDismissClick();
					}}
				>
					<XIcon />
				</IconButton>
			</Tooltip>
		</Stack>
	</MenuItem>
);

export default AlertItem;
